"use client"

import { datadogRum } from "@datadog/browser-rum"
import { useSignup } from "@nelo/signup-lib"
import React, { useState } from "react"

import { OCCUPATION_INCOME_STEP_TEXTS } from "@modules/signup-and-order-flow/components/signup/constants"
import { StepComponentProps } from "@modules/signup-and-order-flow/components/signup/SignupFlowSteps"
import Occupations from "@modules/signup-and-order-flow/util/occupations"

import SelectInput from "../../common/SelectInput"
import StepWrapper from "../../common/StepWrapper"
import TextInput from "../../common/TextInput"

const OccupationTranslations: Record<string, string> = {
  CONTRACTOR: "Contratista",
  PRIVATE_EMPLOYEE: "Empleado privado",
  PUBLIC_EMPLOYEE: "Empleado público",
  SELF_EMPLOYED: "Trabajo por cuenta propia",
  STUDENT: "Estudiante",
  FINANCE_SECTOR: "Sector financiero",
  RETIRED: "Jubilado",
  PROFESSIONAL: "Profesional",
  HOMEMAKER: "Ama de casa",
  PENSIONER: "Pensionado",
  PRIMARY_SECTOR: "Sector primario",
  SECONDARY_SECTOR: "Sector secundario",
  TERTIARY_SECTOR: "Sector terciario",
  UNEMPLOYED: "Desempleado",
  ENTREPRENEUR: "Empresario",
  MERCHANT: "Comerciante",
  OTHER: "Otro",
}

const OccupationIncomeStep: React.FC<StepComponentProps> = ({ onComplete, onBack, progressPercentage, errorHandler, stepId }) => {
  const occupationIncomeText = OCCUPATION_INCOME_STEP_TEXTS

  const { occupation, income } = useSignup()
  const [displayAmount, setDisplayAmount] = useState("$")

  const handleIncomeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/[^\d]/g, "")
    setDisplayAmount(rawValue ? `$${rawValue}` : "$")
    income.setValue(rawValue ? parseInt(rawValue, 10) : 0)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    occupation.setTouched(true)
    occupation.setBlurred(true)
    income.setTouched(true)
    income.setBlurred(true)

    if (!occupation.isValid || !income.isValid) {
      return
    }

    onComplete()
  }

  return (
    <StepWrapper
      title={occupationIncomeText.title}
      subtitle={occupationIncomeText.subtitle}
      progressPercentage={progressPercentage}
      onSubmit={handleSubmit}
      submitDisabled={!occupation.isValid || !income.isValid}
      showBackButton={true}
      onBack={onBack}
      formError={errorHandler.error}
      isLoading={errorHandler.isLoading}
    >
      <div>
        <TextInput
          label={occupationIncomeText.labels.income}
          value={displayAmount}
          onChange={handleIncomeChange}
          onBlur={() => {
            income.setTouched(true)
            income.setBlurred(true)
            if (displayAmount === "") {
              setDisplayAmount("$")
            }
            if (income.errorMessage) {
              datadogRum.addError(new Error(`Income validation error: ${income.errorMessage}`), {
                step: stepId,
                field: "income",
              })
            }
          }}
          onFocus={() => {
            income.setTouched(true)
            const input = document.activeElement as HTMLInputElement
            if (input) {
              setTimeout(() => {
                input.selectionStart = input.selectionEnd = input.value.length
              }, 0)
            }
          }}
          isValid={income.isValid}
          touched={income.touched}
          blurred={income.blurred}
          errorMessage={income.errorMessage}
          inputMode="numeric"
        />

        <SelectInput
          label={occupationIncomeText.labels.occupation}
          options={Occupations.map(({ value, label }) => ({
            value,
            label: OccupationTranslations[value] || label,
          }))}
          value={occupation.value}
          onChange={(e) => occupation.setValue(e.target.value)}
          onBlur={() => {
            occupation.setTouched(true)
            occupation.setBlurred(true)
            if (occupation.errorMessage) {
              datadogRum.addError(new Error(`Occupation validation error: ${occupation.errorMessage}`), {
                step: stepId,
                field: "occupation",
              })
            }
          }}
          onFocus={() => occupation.setTouched(true)}
          isValid={occupation.isValid}
          touched={occupation.touched}
          blurred={occupation.blurred}
          errorMessage={occupation.errorMessage}
        />
      </div>
    </StepWrapper>
  )
}

export default OccupationIncomeStep
